// HQ sign-in gate (spec §3). HQ has no login form: signed-out visitors go to
// the estimator's /sso page, which returns here with #sso_token=<single-use>.
import { supabase } from './supabase.js';
import { ssoStartUrl, readSsoToken } from './links.js';

const LOOP_KEY = 'apb-hq-sso-attempts';

export function showAuthMessage(html) {
  document.body.innerHTML =
    '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;' +
    'background:#080B12;color:#E8ECF3;font-family:system-ui,sans-serif;padding:24px;text-align:center">' +
    `<div style="max-width:420px;line-height:1.5">${html}</div></div>`;
}

export async function gate() {
  const token = readSsoToken(location.hash);
  if (token) {
    history.replaceState(null, '', location.pathname + location.search); // drop token from URL
    let { error } = await supabase.auth.verifyOtp({ token_hash: token, type: 'email' });
    if (error) ({ error } = await supabase.auth.verifyOtp({ token_hash: token, type: 'magiclink' }));
    if (error) console.error('[auth] sso token rejected', error);
  }

  // A session existing locally doesn't prove the Worker accepts it, so the
  // attempt counter is NOT cleared here — markSessionHealthy() does that once
  // a gated API call succeeds. A session the Worker keeps rejecting (401 →
  // local sign-out → reload → handoff) therefore hits the 2-attempt cap.
  const { data } = await supabase.auth.getSession();
  if (data.session) return data.session;

  const startUrl = ssoStartUrl(location.hostname, location.origin + location.pathname);
  const attempts = Number(sessionStorage.getItem(LOOP_KEY) || 0);
  if (!startUrl || attempts >= 2) {
    sessionStorage.removeItem(LOOP_KEY);
    const est = startUrl ? new URL(startUrl).origin : '';
    showAuthMessage(
      'Couldn’t sign you in to HQ automatically.<br><br>' +
      (est ? `<a style="color:#FFB000" href="${est}/">Open the estimator</a>, sign in, then click 📡 HQ.` : 'Unknown host.'),
    );
    return new Promise(() => {}); // stop here
  }
  sessionStorage.setItem(LOOP_KEY, String(attempts + 1));
  location.replace(startUrl);
  return new Promise(() => {}); // navigating away
}

// Called by api.js after a gated API call succeeds: the session works, so
// reset the handoff attempt counter.
export function markSessionHealthy() {
  sessionStorage.removeItem(LOOP_KEY);
}

export async function accessToken() {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

// Drop a dead (revoked/expired) session from this browser only, so the gate
// runs the /sso handoff again instead of reusing it (prevents reload loops).
export async function localSignOut() {
  await supabase.auth.signOut({ scope: 'local' });
}

export async function signOutEverywhere() {
  await supabase.auth.signOut({ scope: 'global' });
  const startUrl = ssoStartUrl(location.hostname, location.origin + '/');
  const est = startUrl ? new URL(startUrl).origin : '';
  showAuthMessage(
    'Signed out of HQ and the estimator.<br><br>' +
    (est ? `<a style="color:#FFB000" href="${est}/">Sign in again</a>` : ''),
  );
}
